/** Shared testimonial carousel click-to-switch (home + europass landings). */

export type TestimonialCarouselOptions = {
  /** Auto-advance interval in ms. Disabled when unset, ≤0, or reduced-motion. */
  autoplayMs?: number;
};

type SlideRole = 'active' | 'prev' | 'next' | 'prev2' | 'next2' | 'prev3' | 'next3';

const SWIPE_THRESHOLD_PX = 56;
const SWIPE_VELOCITY_PX_MS = 0.35;
const CLICK_SUPPRESS_PX = 10;

const ROLE_CLASS: Record<SlideRole, string> = {
  active: 'active',
  prev: 'carousel-item-prev',
  next: 'carousel-item-next',
  prev2: 'carousel-item-prev2',
  next2: 'carousel-item-next2',
  prev3: 'carousel-item-prev3',
  next3: 'carousel-item-next3',
};

const ALL_ROLE_CLASSES = [
  'active',
  'carousel-item-prev',
  'carousel-item-next',
  'carousel-item-prev2',
  'carousel-item-next2',
  'carousel-item-prev3',
  'carousel-item-next3',
  'carousel-item-next4',
  'carousel-item-next5',
  'carousel-item-next6',
  'carousel-item-next7',
  'carousel-item-next8',
  'carousel-item-next9',
  'carousel-item-next10',
  'carousel-item-far',
  'is-passing-behind',
];

export function initTestimonialCarousel(
  root: ParentNode = document,
  options: TestimonialCarouselOptions = {},
): () => void {
  const carouselItems = root.querySelectorAll(
    '.testimonial-section .carousel-item',
  );
  if (carouselItems.length === 0) {
    return () => undefined;
  }

  const section =
    (root instanceof Element
      ? root.closest('.testimonial-section')
      : null) ??
    root.querySelector('.testimonial-section') ??
    carouselItems[0]?.closest('.testimonial-section');

  const carouselInner =
    section?.querySelector('.carousel-inner') ??
    root.querySelector('.testimonial-section .carousel-inner');

  let currentSlide = 0;
  let autoplayTimer: ReturnType<typeof setInterval> | null = null;
  let heightSynced = false;
  let prefersReducedMotion = false;
  let isAnimating = false;
  let animatingTimer: ReturnType<typeof setTimeout> | null = null;

  if (typeof window !== 'undefined' && window.matchMedia) {
    prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
  }

  const syncTrackHeight = () => {
    if (!(carouselInner instanceof HTMLElement) || heightSynced) {
      return;
    }
    let maxHeight = 0;
    carouselItems.forEach((item) => {
      maxHeight = Math.max(maxHeight, (item as HTMLElement).offsetHeight);
    });
    if (maxHeight > 0) {
      carouselInner.style.height = `${maxHeight + 48}px`;
      heightSynced = true;
    }
  };

  const markAnimating = () => {
    if (!(carouselInner instanceof HTMLElement) || prefersReducedMotion) {
      return;
    }
    isAnimating = true;
    carouselInner.classList.add('is-animating');
    if (animatingTimer !== null) {
      clearTimeout(animatingTimer);
    }
    animatingTimer = setTimeout(() => {
      isAnimating = false;
      carouselInner.classList.remove('is-animating');
      animatingTimer = null;
    }, 1200);
  };

  const rolesForIndex = (index: number): Map<number, SlideRole> => {
    const len = carouselItems.length;
    const nextRoles = new Map<number, SlideRole>();
    const assign = (i: number, role: SlideRole) => {
      if (i < 0 || i >= len || nextRoles.has(i)) {
        return;
      }
      nextRoles.set(i, role);
    };

    assign(index, 'active');
    assign((index - 1 + len) % len, 'prev');
    assign((index + 1) % len, 'next');
    if (len >= 5) {
      assign((index - 2 + len) % len, 'prev2');
      assign((index + 2) % len, 'next2');
    }
    if (len >= 7) {
      assign((index - 3 + len) % len, 'prev3');
      assign((index + 3) % len, 'next3');
    }
    return nextRoles;
  };

  const showSlide = (index: number) => {
    const nextRoles = rolesForIndex(index);
    const zByRole: Partial<Record<SlideRole, string>> = {
      active: '5',
      prev: '3',
      next: '3',
      prev2: '2',
      next2: '2',
      prev3: '1',
      next3: '1',
    };

    // Apply roles in one pass — never drop to the base transform mid-frame.
    carouselItems.forEach((item, i) => {
      const slide = item as HTMLElement;
      const role = nextRoles.get(i);
      ALL_ROLE_CLASSES.forEach((className) => slide.classList.remove(className));
      if (role) {
        slide.classList.add(ROLE_CLASS[role]);
        slide.style.zIndex = zByRole[role] ?? '';
      } else {
        slide.style.zIndex = '0';
        slide.classList.add('carousel-item-far');
      }
    });

    markAnimating();
    syncTrackHeight();
  };

  const goTo = (index: number) => {
    const next =
      ((index % carouselItems.length) + carouselItems.length) %
      carouselItems.length;
    if (next === currentSlide) {
      return;
    }
    currentSlide = next;
    showSlide(currentSlide);
  };

  const listeners: Array<{
    el: Element | Document;
    type: string;
    handler: EventListener;
    options?: AddEventListenerOptions | boolean;
  }> = [];

  const on = (
    el: Element | Document,
    type: string,
    handler: EventListener,
    options?: AddEventListenerOptions | boolean,
  ) => {
    el.addEventListener(type, handler, options);
    listeners.push({ el, type, handler, options });
  };

  let suppressClickUntil = 0;

  const activateItem = (index: number) => {
    goTo(index);
    restartAutoplay();
  };

  // Click/tap side cards → bring to front (also used after a clean pointer-up).
  carouselItems.forEach((item, index) => {
    on(item, 'click', (event) => {
      if (Date.now() < suppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (!item.classList.contains('active')) {
        activateItem(index);
      }
    });
  });

  // --- Pointer swipe (finger + mouse) ---
  let pointerId: number | null = null;
  let startX = 0;
  let startY = 0;
  let lastX = 0;
  let lastT = 0;
  let velocityX = 0;
  let isHorizontalSwipe: boolean | null = null;
  let dragDistance = 0;
  let pointerDownIndex: number | null = null;

  const swipeTarget =
    carouselInner instanceof HTMLElement
      ? carouselInner
      : section instanceof HTMLElement
        ? section
        : null;

  const clearSwipeStyles = (animateBack: boolean) => {
    if (!(swipeTarget instanceof HTMLElement)) {
      return;
    }
    if (animateBack) {
      // Re-enable transitions first, then ease swipe offset back to 0.
      swipeTarget.classList.remove('is-swiping');
      swipeTarget.style.setProperty('--kw-swipe-x', '0px');
      window.setTimeout(() => {
        if (swipeTarget.style.getPropertyValue('--kw-swipe-x') === '0px') {
          swipeTarget.style.removeProperty('--kw-swipe-x');
        }
      }, 50);
    } else {
      swipeTarget.classList.remove('is-swiping');
      swipeTarget.style.removeProperty('--kw-swipe-x');
    }
  };

  const setSwipeOffset = (dx: number) => {
    if (!(swipeTarget instanceof HTMLElement)) {
      return;
    }
    const resisted = Math.max(-160, Math.min(160, dx * 0.85));
    swipeTarget.style.setProperty('--kw-swipe-x', `${resisted}px`);
  };

  const stopAutoplay = () => {
    if (autoplayTimer !== null) {
      clearInterval(autoplayTimer);
      autoplayTimer = null;
    }
  };

  const startAutoplay = () => {
    stopAutoplay();
    const ms = options.autoplayMs;
    if (!ms || ms <= 0 || prefersReducedMotion || carouselItems.length < 2) {
      return;
    }
    autoplayTimer = setInterval(() => {
      goTo(currentSlide + 1);
    }, ms);
  };

  const restartAutoplay = () => {
    startAutoplay();
  };

  const releasePointer = (id: number) => {
    if (
      swipeTarget instanceof HTMLElement &&
      typeof swipeTarget.hasPointerCapture === 'function' &&
      swipeTarget.hasPointerCapture(id) &&
      typeof swipeTarget.releasePointerCapture === 'function'
    ) {
      swipeTarget.releasePointerCapture(id);
    }
  };

  const endSwipe = (event: PointerEvent) => {
    if (pointerId === null || event.pointerId !== pointerId) {
      return;
    }

    const dx = event.clientX - startX;
    const moveDx = lastX - startX;
    const effectiveDx = Math.abs(dx) > Math.abs(moveDx) ? dx : moveDx;
    const instantVelocity = Math.abs(velocityX);
    const wasHorizontal = isHorizontalSwipe === true;
    const shouldAdvance =
      wasHorizontal &&
      (Math.abs(effectiveDx) >= SWIPE_THRESHOLD_PX ||
        instantVelocity >= SWIPE_VELOCITY_PX_MS);
    const wasTap =
      !wasHorizontal && dragDistance < CLICK_SUPPRESS_PX && pointerDownIndex !== null;

    releasePointer(pointerId);
    pointerId = null;

    if (shouldAdvance && carouselItems.length > 1) {
      // Keep transitions on; zero swipe offset in the same turn as role change.
      if (swipeTarget instanceof HTMLElement) {
        swipeTarget.classList.remove('is-swiping');
        swipeTarget.style.setProperty('--kw-swipe-x', '0px');
      }
      goTo(currentSlide + (effectiveDx < 0 ? 1 : -1));
      window.setTimeout(() => {
        swipeTarget?.style.removeProperty('--kw-swipe-x');
      }, 30);
      suppressClickUntil = Date.now() + 500;
      restartAutoplay();
    } else if (wasTap && pointerDownIndex !== null) {
      clearSwipeStyles(false);
      // Clicking a background card brings it to the foreground.
      if (pointerDownIndex !== currentSlide) {
        activateItem(pointerDownIndex);
        suppressClickUntil = Date.now() + 400;
      } else {
        restartAutoplay();
      }
    } else {
      clearSwipeStyles(true);
      if (dragDistance >= CLICK_SUPPRESS_PX) {
        suppressClickUntil = Date.now() + 300;
      }
      restartAutoplay();
    }

    isHorizontalSwipe = null;
    dragDistance = 0;
    velocityX = 0;
    pointerDownIndex = null;
  };

  if (swipeTarget instanceof HTMLElement && carouselItems.length > 1) {
    swipeTarget.classList.add('kw-swipeable');
    swipeTarget.style.touchAction = 'pan-y';

    on(swipeTarget, 'pointerdown', ((event: PointerEvent) => {
      if (event.button !== 0 && event.pointerType === 'mouse') {
        return;
      }
      if (isAnimating) {
        // Allow interaction, but ignore if mid-settle from a prior move? Better allow.
      }
      const targetItem = (event.target as Element | null)?.closest?.(
        '.carousel-item',
      );
      pointerDownIndex = targetItem
        ? [...carouselItems].indexOf(targetItem)
        : null;

      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      lastX = event.clientX;
      lastT = event.timeStamp;
      velocityX = 0;
      isHorizontalSwipe = null;
      dragDistance = 0;
      stopAutoplay();
      if (typeof swipeTarget.setPointerCapture === 'function') {
        swipeTarget.setPointerCapture(event.pointerId);
      }
    }) as EventListener);

    on(
      swipeTarget,
      'pointermove',
      ((event: PointerEvent) => {
        if (pointerId === null || event.pointerId !== pointerId) {
          return;
        }

        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        dragDistance = Math.max(dragDistance, Math.abs(dx), Math.abs(dy));

        if (isHorizontalSwipe === null) {
          if (Math.abs(dx) < 10 && Math.abs(dy) < 10) {
            return;
          }
          isHorizontalSwipe = Math.abs(dx) > Math.abs(dy) * 1.15;
          if (!isHorizontalSwipe) {
            releasePointer(pointerId);
            pointerId = null;
            clearSwipeStyles(false);
            return;
          }
          swipeTarget.classList.add('is-swiping');
        }

        if (!isHorizontalSwipe) {
          return;
        }

        event.preventDefault();
        const dt = Math.max(1, event.timeStamp - lastT);
        velocityX = (event.clientX - lastX) / dt;
        lastX = event.clientX;
        lastT = event.timeStamp;
        setSwipeOffset(dx);
      }) as EventListener,
      { passive: false },
    );

    on(swipeTarget, 'pointerup', endSwipe as EventListener);
    on(swipeTarget, 'pointercancel', ((event: PointerEvent) => {
      if (pointerId === null || event.pointerId !== pointerId) {
        return;
      }
      releasePointer(pointerId);
      pointerId = null;
      clearSwipeStyles(true);
      isHorizontalSwipe = null;
      dragDistance = 0;
      pointerDownIndex = null;
      restartAutoplay();
    }) as EventListener);
  }

  if (section && options.autoplayMs && options.autoplayMs > 0) {
    on(section, 'mouseenter', () => stopAutoplay());
    on(section, 'mouseleave', () => {
      if (pointerId === null) {
        startAutoplay();
      }
    });
    on(section, 'focusin', () => stopAutoplay());
    on(section, 'focusout', () => startAutoplay());
  }

  const onResize = () => {
    heightSynced = false;
    syncTrackHeight();
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', onResize);
  }

  let intersectionObserver: IntersectionObserver | null = null;
  let enterTimer: ReturnType<typeof setTimeout> | null = null;

  const markInView = () => {
    if (!section) {
      return;
    }
    section.classList.add('is-in-view');
    if (enterTimer === null && !section.classList.contains('has-entered')) {
      enterTimer = setTimeout(() => {
        section.classList.add('has-entered');
        enterTimer = null;
      }, 200);
    }
    startAutoplay();
  };

  if (section) {
    section.classList.add('kw-carousel-ready');
  }

  if (section && typeof IntersectionObserver !== 'undefined') {
    intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            markInView();
          } else {
            stopAutoplay();
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -8% 0px' },
    );
    intersectionObserver.observe(section);

    const rect = section.getBoundingClientRect();
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;
    if (rect.top < viewportHeight * 0.85 && rect.bottom > viewportHeight * 0.1) {
      markInView();
    }
  } else {
    markInView();
    if (section) {
      section.classList.add('has-entered');
    }
  }

  showSlide(currentSlide);
  if (typeof requestAnimationFrame !== 'undefined') {
    requestAnimationFrame(() => syncTrackHeight());
  } else {
    syncTrackHeight();
  }

  return () => {
    stopAutoplay();
    clearSwipeStyles(false);
    if (enterTimer !== null) {
      clearTimeout(enterTimer);
    }
    if (animatingTimer !== null) {
      clearTimeout(animatingTimer);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', onResize);
    }
    intersectionObserver?.disconnect();
    listeners.forEach(({ el, type, handler, options: opts }) => {
      el.removeEventListener(type, handler, opts);
    });
  };
}
